import {
    Box,
    Button,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Image,
    Badge,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    Select,
    Checkbox,
    VStack,
    useToast,
    Spinner,
    Text,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import axios from "axios";
import { api_url } from "../../url";

export default function BannerManager() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [games, setGames] = useState([]);
    const [gameSearchResults, setGameSearchResults] = useState([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedBanner, setSelectedBanner] = useState(null);
    const toast = useToast();

    const [formData, setFormData] = useState({
        title: "",
        image: "",
        useGameThumbnail: false,
        game: "",
        linkType: "game",
        customLink: "",
        status: true,
        order: 0,
    });

    const fetchBanners = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const { data } = await axios.get(`${api_url}/frontend/banners`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (data.success) {
                setBanners(data.data);
            }
        } catch (error) {
            console.error("Failed to fetch banners", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGames = async () => {
        try {
            const token = localStorage.getItem("token");
            const { data } = await axios.get(`${api_url}/games?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (data.success) {
                setGames(data.data);
            }
        } catch (error) {
            console.error("Failed to fetch games", error);
        }
    };

    useEffect(() => {
        fetchBanners();
        fetchGames();
    }, []);

    const handleOpenModal = (banner = null) => {
        if (banner) {
            setSelectedBanner(banner);
            setFormData({
                title: banner.title || "",
                image: banner.image || "",
                useGameThumbnail: banner.useGameThumbnail || false,
                game: banner.game?._id || banner.game || "",
                linkType: banner.linkType || "game",
                customLink: banner.customLink || "",
                status: banner.status ?? true,
                order: banner.order || 0,
            });
        } else {
            setSelectedBanner(null);
            setFormData({
                title: "",
                image: "",
                useGameThumbnail: false,
                game: "",
                linkType: "game",
                customLink: "",
                status: true,
                order: 0,
            });
        }
        onOpen();
    };

    const handleSubmit = async () => {
        try {
            const token = localStorage.getItem("token");
            const payload = { ...formData };
            if (payload.linkType === "custom" || !payload.game || payload.game === "") {
                payload.game = null;
            }

            let res;
            if (selectedBanner) {
                res = await axios.put(`${api_url}/frontend/banners/${selectedBanner._id}`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                res = await axios.post(`${api_url}/frontend/banners`, payload, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            if (res.data.success) {
                toast({
                    title: selectedBanner ? "Banner Updated" : "Banner Created",
                    status: "success",
                    duration: 3000,
                });
                fetchBanners();
                onClose();
            }
        } catch (error) {
            toast({
                title: "Operation Failed",
                description: error.response?.data?.message || error.message,
                status: "error",
            });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this banner?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${api_url}/frontend/banners/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast({ title: "Banner Deleted", status: "success" });
            fetchBanners();
        } catch (error) {
            toast({ title: "Delete Failed", status: "error" });
        }
    };

    return (
        <Box>
            <Flex justify="flex-end" mb={4}>
                <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={() => handleOpenModal()}>
                    Add Banner
                </Button>
            </Flex>

            {loading ? (
                <Flex justify="center" py={10}>
                    <Spinner />
                </Flex>
            ) : (
                <Table variant="simple">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th>Image</Th>
                            <Th>Title</Th>
                            <Th>Link To</Th>
                            <Th>Order</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {banners.map((b) => (
                            <Tr key={b._id}>
                                <Td>
                                    <Image
                                        src={b.useGameThumbnail ? b.game?.thumbnail : b.image}
                                        boxSize="80px"
                                        objectFit="cover"
                                        borderRadius="md"
                                        fallbackSrc="https://via.placeholder.com/80x40"
                                    />
                                </Td>
                                <Td fontWeight="600">{b.title || "No Title"}</Td>
                                <Td size="sm">
                                    {b.linkType === "game" ? `Game: ${b.game?.name || "N/A"}` : `Custom: ${b.customLink}`}
                                </Td>
                                <Td>{b.order}</Td>
                                <Td>
                                    <Badge colorScheme={b.status ? "green" : "red"}>{b.status ? "Active" : "Inactive"}</Badge>
                                </Td>
                                <Td>
                                    <Flex gap={2}>
                                        <IconButton size="sm" icon={<FiEdit />} onClick={() => handleOpenModal(b)} />
                                        <IconButton size="sm" icon={<FiTrash2 />} onClick={() => handleDelete(b._id)} colorScheme="red" variant="ghost" />
                                    </Flex>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            )}

            {/* Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{selectedBanner ? "Edit Banner" : "Add New Banner"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <FormControl>
                                <FormLabel>Title</FormLabel>
                                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="Promotion Title" />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Link Type</FormLabel>
                                <Select value={formData.linkType} onChange={(e) => setFormData({ ...formData, linkType: e.target.value })}>
                                    <option value="game">Launch Game</option>
                                    <option value="custom">Custom URL</option>
                                </Select>
                            </FormControl>

                            {formData.linkType === "game" && (
                                <FormControl>
                                    <FormLabel>Game Selection</FormLabel>
                                    <Flex gap={2} mb={2}>
                                        <Input
                                            placeholder="Search game name..."
                                            onChange={(e) => {
                                                const term = e.target.value.toLowerCase();
                                                const filtered = games.filter(g => g.name.toLowerCase().includes(term)).slice(0, 10);
                                                setGameSearchResults(filtered);
                                            }}
                                        />
                                        <Input
                                            placeholder="Or enter Game ID"
                                            width="200px"
                                            value={formData.game}
                                            onChange={(e) => setFormData({ ...formData, game: e.target.value })}
                                        />
                                    </Flex>

                                    {gameSearchResults.length > 0 && (
                                        <Box border="1px solid #eee" borderRadius="md" mb={2} maxHeight="150px" overflowY="auto">
                                            {gameSearchResults.map(g => (
                                                <Flex
                                                    key={g._id}
                                                    p={2}
                                                    _hover={{ bg: "gray.50" }}
                                                    cursor="pointer"
                                                    onClick={() => {
                                                        setFormData({ ...formData, game: g._id });
                                                        setGameSearchResults([]);
                                                    }}
                                                    justify="space-between"
                                                    align="center"
                                                >
                                                    <Text fontSize="sm">{g.name} ({g.provider?.name})</Text>
                                                    <Badge fontSize="xs">{g._id}</Badge>
                                                </Flex>
                                            ))}
                                        </Box>
                                    )}

                                    {formData.game && (
                                        <Box p={2} bg="blue.50" borderRadius="md">
                                            <Text fontSize="xs" fontWeight="bold">Selected Game ID:</Text>
                                            <Text fontSize="xs">{formData.game}</Text>
                                            {games.find(g => g._id === formData.game) && (
                                                <Text fontSize="sm" color="blue.600">
                                                    {games.find(g => g._id === formData.game).name}
                                                </Text>
                                            )}
                                        </Box>
                                    )}
                                </FormControl>
                            )}

                            {formData.linkType === "custom" && (
                                <FormControl>
                                    <FormLabel>Custom Link</FormLabel>
                                    <Input value={formData.customLink} onChange={(e) => setFormData({ ...formData, customLink: e.target.value })} placeholder="https://..." />
                                </FormControl>
                            )}

                            <FormControl>
                                <Checkbox
                                    isChecked={formData.useGameThumbnail}
                                    onChange={(e) => setFormData({ ...formData, useGameThumbnail: e.target.checked })}
                                    isDisabled={formData.linkType !== "game" || !formData.game}
                                >
                                    Keep game default thumbnail
                                </Checkbox>
                            </FormControl>

                            {!formData.useGameThumbnail && (
                                <FormControl>
                                    <FormLabel>Banner Image URL</FormLabel>
                                    <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} placeholder="Image URL" />
                                </FormControl>
                            )}

                            <Flex w="100%" gap={4}>
                                <FormControl>
                                    <FormLabel>Order</FormLabel>
                                    <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} />
                                </FormControl>
                                <FormControl display="flex" alignItems="center" pt={8}>
                                    <Checkbox isChecked={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.checked })}>
                                        Active
                                    </Checkbox>
                                </FormControl>
                            </Flex>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSubmit}>
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
